import { supabase } from '@/integrations/supabase/client';
import { getAccessToken } from '@/shared/utils/supabaseStorage';

export class APIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public code?: string
  ) {
    super(message);
    this.name = 'APIError';
  }
}

interface RequestOptions extends RequestInit {
  timeout?: number;
  retryAttempts?: number;
}

class APIClient {
  private baseURL: string;
  private pendingRequests: Map<string, { promise: Promise<any>; timestamp: number }>;
  private defaultTimeout: number;
  private defaultRetryAttempts: number;
  private requestCacheTimeout: number;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.pendingRequests = new Map();
    this.defaultTimeout = 30000; // 30 seconds
    this.defaultRetryAttempts = 1;
    this.requestCacheTimeout = 35000; // Clear stuck requests after 35 seconds (longer than defaultTimeout)
  }

  /**
   * Get authorization token from localStorage
   * CRITICAL FIX: Use localStorage instead of getSession() to prevent hangs
   * localStorage is synchronous and works even when tab is inactive
   */
  private async getAuthToken(): Promise<string | null> {
    try {
      // Use shared utility to dynamically find Supabase auth key
      return getAccessToken();
    } catch (error) {
      console.error('[APIClient] Error getting auth token:', error);
      return null;
    }
  }

  /**
   * Refresh the Supabase session and return new token
   */
  private async refreshToken(): Promise<string | null> {
    try {
      const { data: { session }, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Error refreshing session:', error);
        return null;
      }
      return session?.access_token || null;
    } catch (error) {
      console.error('Error in refreshToken:', error);
      return null;
    }
  }

  /**
   * Make HTTP request with timeout and error handling
   */
  private async fetchWithTimeout(
    url: string,
    options: RequestOptions = {}
  ): Promise<Response> {
    const { timeout = this.defaultTimeout, signal, ...fetchOptions } = options;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...fetchOptions,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      return response;
    } catch (error: any) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new APIError('Request timeout', 408, 'TIMEOUT');
      }
      throw error;
    }
  }

  /**
   * Handle API response and errors
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      let errorMessage = `Request failed with status ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch {
        // Response body is not JSON
      }
      throw new APIError(errorMessage, response.status, 'API_ERROR');
    }

    try {
      return await response.json();
    } catch (error) {
      throw new APIError('Invalid JSON response', response.status, 'INVALID_JSON');
    }
  }

  /**
   * Make authenticated request with retry logic
   */
  private async makeRequest<T>(
    method: string,
    endpoint: string,
    options: RequestOptions = {},
    attemptNumber = 0
  ): Promise<T> {
    const { retryAttempts = this.defaultRetryAttempts, ...requestOptions } = options;

    try {
      // Get auth token
      const token = await this.getAuthToken();

      console.log('[APIClient] Making request:', method, endpoint, {
        hasToken: !!token,
        tokenLength: token?.length || 0,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
      });

      // Build headers
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...(requestOptions.headers || {}),
      };

      // Add auth token if available (matching mobile app behavior)
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('[APIClient] Added Authorization header');
      } else {
        console.warn('[APIClient] No token available - request will be unauthenticated');
      }

      // Make request
      const url = `${this.baseURL}${endpoint}`;
      const response = await this.fetchWithTimeout(url, {
        ...requestOptions,
        method,
        headers,
      });

      // Handle 401 Unauthorized - try to refresh token (matching mobile app)
      if (response.status === 401 && token && attemptNumber === 0) {
        console.log('[APIClient] Token expired, attempting refresh...');

        // Attempt to refresh session
        const newToken = await this.refreshToken();

        if (newToken) {
          console.log('[APIClient] Token refreshed successfully, retrying request...');

          // Retry request with new token
          const newHeaders: HeadersInit = {
            'Content-Type': 'application/json',
            ...requestOptions.headers,
            Authorization: `Bearer ${newToken}`,
          };

          const retryResponse = await this.fetchWithTimeout(url, {
            ...requestOptions,
            method,
            headers: newHeaders,
          });

          return await this.handleResponse<T>(retryResponse);
        } else {
          // Refresh failed - user needs to log in again
          console.error('[APIClient] Token refresh failed, session expired');

          // Sign out WITHOUT hard redirect (let AuthContext handle navigation)
          await supabase.auth.signOut();

          // Throw error with special code that AuthContext will catch
          throw new APIError('Session expired. Please log in again.', 401, 'SESSION_EXPIRED');
        }
      }

      return await this.handleResponse<T>(response);
    } catch (error: any) {
      // Don't retry if it's already a retry attempt
      if (error instanceof APIError && error.statusCode === 401) {
        throw error;
      }

      // Retry on network errors (but not on API errors)
      if (
        attemptNumber < retryAttempts &&
        (error.code === 'TIMEOUT' || error.name === 'TypeError')
      ) {
        // Wait 1 second before retry
        await new Promise(resolve => setTimeout(resolve, 1000));
        return this.makeRequest<T>(method, endpoint, options, attemptNumber + 1);
      }

      throw error;
    }
  }

  /**
   * GET request with request deduplication
   */
  async get<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    // Create cache key for request deduplication
    const cacheKey = `GET:${endpoint}`;

    // Check if request is already pending and not stale
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      const age = Date.now() - pendingRequest.timestamp;

      // If request is too old, clear it and make a fresh request
      if (age > this.requestCacheTimeout) {
        console.log(`[APIClient] Clearing stale pending request (${age}ms old): ${cacheKey}`);
        this.pendingRequests.delete(cacheKey);
      } else {
        console.log(`[APIClient] Reusing pending request (${age}ms old): ${cacheKey}`);
        return pendingRequest.promise;
      }
    }

    // Make new request
    const requestPromise = this.makeRequest<T>('GET', endpoint, options)
      .finally(() => {
        // Remove from pending requests when complete
        this.pendingRequests.delete(cacheKey);
      });

    // Store in pending requests with timestamp
    this.pendingRequests.set(cacheKey, {
      promise: requestPromise,
      timestamp: Date.now()
    });

    return requestPromise;
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, body?: any, options: RequestOptions = {}): Promise<T> {
    return this.makeRequest<T>('POST', endpoint, {
      ...options,
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, body?: any, options: RequestOptions = {}): Promise<T> {
    return this.makeRequest<T>('PUT', endpoint, {
      ...options,
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, body?: any, options: RequestOptions = {}): Promise<T> {
    return this.makeRequest<T>('PATCH', endpoint, {
      ...options,
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.makeRequest<T>('DELETE', endpoint, options);
  }

  /**
   * Clear all pending requests (useful for cleanup)
   */
  clearPendingRequests(): void {
    this.pendingRequests.clear();
  }
}

// Create singleton instance
const API_BASE_URL = import.meta.env.VITE_SIGNALING_SERVER_URL ?? 'https://webrtc.wlkthru.co';
export const apiClient = new APIClient(`${API_BASE_URL}/api`);

// Export type for use in other files
export type { RequestOptions };
